import type { ClientFlowAction } from '~/graphql/types.client';
import type {
  FlowData___FlowInstanceFragment,
  FlowData___SubjectFragment,
} from '~/graphql/types';
import type { SubjectMap } from '~/components/page/Automation/v2/components/UpdateAction/components/Selector/utils/getSubject';
import type {
  ConditionMap,
  SubjectToConditionMap,
} from '../../../UpdateAction/components/Selector/utils/getConditions';

import {
  DirectoryMap,
  generateDirectoryMap,
} from '~/components/page/Automation/v2/components/UpdateAction/components/Selector/utils/getDirectory';
import {
  generateInstanceMap,
  InstanceMap,
} from '~/components/page/Automation/v2/components/UpdateAction/components/Selector/utils/getInstance';
import type { PrimitiveInputMap } from '../../../UpdateAction/components/Selector/utils/getPrimitiveInput';
import getParentInstances from '../../getParentInstances';

export type RelativeMaps = {
  instanceMap: InstanceMap;
  subjectMap: SubjectMap;
  directoryMap: DirectoryMap;
  primitiveInputMap: PrimitiveInputMap;
  conditionMap: ConditionMap;
  subjectToConditionMap: SubjectToConditionMap;
  /** Initial instances retrieved from the BE */
  instances: Array<FlowData___FlowInstanceFragment>;
  subjects: Array<FlowData___SubjectFragment>;
};

type GetRelativeMapsArgs = {
  /** The action until which the instances need to be collected */
  action?: ClientFlowAction;

  actions: Array<ClientFlowAction>;

  maps: RelativeMaps;
};

const getRelativeMaps = ({
  action,
  actions,
  maps,
}: GetRelativeMapsArgs): RelativeMaps => {
  const {
    subjectMap,
    instanceMap,
    primitiveInputMap,
    conditionMap,
    subjectToConditionMap,
    subjects,
    instances,
  } = maps;

  const actionInstances = getParentInstances({
    action,
    allActions: actions,
    subjectMap,
  });

  const relativeInstanceMap = generateInstanceMap(actionInstances);

  const completeInstanceMap: InstanceMap = {
    ...instanceMap,
    ...relativeInstanceMap,
  };

  const relativeDirectoryMap: DirectoryMap = generateDirectoryMap(
    subjects,
    [...actionInstances, ...instances],
    subjectMap,
  );

  return {
    instanceMap: completeInstanceMap,
    directoryMap: relativeDirectoryMap,
    subjectMap,
    primitiveInputMap,
    conditionMap,
    subjectToConditionMap,
    instances,
    subjects,
  };
};

export default getRelativeMaps;
